import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  PermissionCatalogueResponse,
  RoleListResponse,
  RoleShowResponse,
  UserScopesResponse,
} from '../../../Interfaces/api/backoffice/rbac.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';
import { PermissionGrant } from '../../../Models/backoffice/permission-grant.model';
import { PermissionScope } from '../../../Models/backoffice/permission-scope.model';
import { RoleSummary } from '../../../Models/backoffice/role-summary.model';

@Injectable({
  providedIn: 'root',
})
export class RbacService {
  private readonly _apiService = inject(ApiService);

  rolesRequest(
    search: string,
    organizationId: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<RoleListResponse>> {
    const params: Record<string, string | number> = { page, pageSize };

    if (search) {
      params['search'] = search;
    }

    if (organizationId) {
      params['organization_id'] = organizationId;
    }

    return this._apiService.get<LaravelDefaultResponse<RoleListResponse>>(
      endpoints.backoffice.rbacRoles,
      { params },
    );
  }

  roleRequest(roleId: string): TypedResourceRequest<LaravelDefaultResponse<RoleShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<RoleShowResponse>>(
      endpoints.backoffice.rbacRole(roleId),
      { skipLoading: true },
    );
  }

  permissionsRequest(): TypedResourceRequest<LaravelDefaultResponse<PermissionCatalogueResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PermissionCatalogueResponse>>(
      endpoints.backoffice.rbacPermissions,
      { skipLoading: true },
    );
  }

  userScopesRequest(
    userId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<UserScopesResponse>> {
    return this._apiService.get<LaravelDefaultResponse<UserScopesResponse>>(
      endpoints.backoffice.rbacUserScopes(userId),
      { skipLoading: true, skipError: true },
    );
  }

  toRolesPage(
    response: LaravelDefaultResponse<RoleListResponse> | undefined,
  ): PagedResult<RoleSummary> {
    const payload = response?.data;

    return {
      items: (payload?.roles ?? []).map((row) => new RoleSummary(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toRoleDetail(response: LaravelDefaultResponse<RoleShowResponse> | undefined): RoleSummary | null {
    return response?.data?.role ? new RoleSummary(response.data.role) : null;
  }

  toPermissionCatalogue(
    response: LaravelDefaultResponse<PermissionCatalogueResponse> | undefined,
  ): PermissionGrant[] {
    return (response?.data?.permissions ?? []).map((row) => new PermissionGrant(row));
  }

  toRolePermissions(
    response: LaravelDefaultResponse<UserScopesResponse> | undefined,
  ): PermissionGrant[] {
    return (response?.data?.role_permissions ?? []).map((row) => new PermissionGrant(row));
  }

  toUserScopes(
    response: LaravelDefaultResponse<UserScopesResponse> | undefined,
  ): PermissionScope[] {
    return (response?.data?.scopes ?? []).map((row) => new PermissionScope(row));
  }
}
